<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue'
import introJs from 'intro.js'
import 'intro.js/introjs.css'
import '@/styles/introjs-agentwall.css'

export interface TourStep {
  element: string
  intro: string
  title?: string
  position?: 'top' | 'bottom' | 'left' | 'right'
}

const props = withDefaults(
  defineProps<{
    steps: TourStep[]
    tourId: string
    autoStart?: boolean
    doneLabel?: string
  }>(),
  {
    autoStart: false,
    doneLabel: 'Finalizar',
  }
)

const STORAGE_KEY = 'toursCompleted'

const tourInstance = ref<ReturnType<typeof introJs.tour> | null>(null)
const isRunning = ref(false)

function getCompleted(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
  } catch {
    return {}
  }
}

function setCompleted(id: string, completed: boolean) {
  const data = getCompleted()
  data[id] = completed
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
}

function cleanup() {
  if (tourInstance.value) {
    try {
      void tourInstance.value.exit(true)
    } catch {
      /* ignore */
    }
    tourInstance.value = null
  }
  isRunning.value = false
}

async function startTour() {
  if (!props.steps.length || isRunning.value) return

  cleanup()

  const steps = props.steps.map((step) => ({
    title: step.title || '',
    intro: step.intro,
    element: step.element,
    position: step.position || 'bottom',
  }))

  const intro = introJs.tour()
  await intro.setOptions({
    steps: steps as any,
    nextLabel: 'Siguiente',
    prevLabel: 'Anterior',
    skipLabel: '✕',
    doneLabel: props.doneLabel,
    tooltipClass: 'agentwall-tooltip',
    highlightClass: 'agentwall-highlight',
    exitOnEsc: true,
    exitOnOverlayClick: true,
    showStepNumbers: false,
    showProgress: true,
    showBullets: false,
    keyboardNavigation: true,
    scrollToElement: true,
    scrollTo: 'element' as any,
    autoPosition: true,
    overlayOpacity: 0.55,
    helperElementPadding: 6,
    disableInteraction: false,
    scrollPadding: 60,
  } as any)

  intro
    .onComplete(() => {
      setCompleted(props.tourId, true)
      cleanup()
    })
    .onSkip(() => {
      setCompleted(props.tourId, true)
      cleanup()
    })
    .onExit(() => {
      setCompleted(props.tourId, true)
      cleanup()
    })

  tourInstance.value = intro
  isRunning.value = true
  await intro.start()
}

function restart() {
  setCompleted(props.tourId, false)
  void startTour()
}

function maybeAutoStart() {
  if (!props.autoStart) return
  if (getCompleted()[props.tourId]) return
  setTimeout(() => void startTour(), 500)
}

watch(
  () => props.tourId,
  (newId, oldId) => {
    if (newId !== oldId) {
      cleanup()
      if (props.autoStart) maybeAutoStart()
    }
  }
)

onMounted(() => {
  if (props.autoStart) maybeAutoStart()
})

onUnmounted(() => {
  cleanup()
})

defineExpose({
  restart,
  start: startTour,
  exit: cleanup,
  isRunning: () => isRunning.value,
})
</script>

<template>
  <div class="hidden" aria-hidden="true" />
</template>

<style>
/* Fix crítico: intro.js usa position:absolute por defecto, lo que causa
   duplicación visual en layouts con overflow-hidden */
.introjs-overlay {
  position: fixed !important;
  z-index: 999998 !important;
}

.introjs-helperLayer {
  position: fixed !important;
  z-index: 999999 !important;
}

.introjs-tooltipReferenceLayer,
.introjs-tooltip {
  position: fixed !important;
  z-index: 1000000 !important;
}
</style>
